<!-- This component is used to display the right side bar of the Signing Flow, which contains the shopping cart and rate calculator. -->

<template>
	<section
		class="transition-width bg-background-50! relative flex justify-end py-6 duration-300"
	>
		<!-- Expand Button -->
		<button
			v-if="!expanded"
			type="button"
			class="bg-background-50! absolute top-8 -left-6 py-2"
			@click="emit('expand')"
		>
			<ChevronLeftIcon class="text-primary-700! h-6 w-6 text-xs" />
		</button>

		<!-- Main Expandable Content -->
		<div
			:class="expanded ? 'w-full opacity-100' : 'w-0 opacity-0'"
			class="relative mb-8 h-screen min-h-0 overflow-auto transition-all duration-300"
		>
			<Transition
				class="transition-all duration-300"
				enter-from-class="-left-72 opacity-0"
				enter-to-class="left-0 opacity-100"
				leave-from-class="left-0 opacity-100"
				leave-to-class="-left-72 opacity-0"
			>
				<div v-if="expanded">
					<!-- Dropdown -->
					<div class="relative w-full pl-4">
						<div
							class="group hover:border-primary-700 hover:text-primary-700 flex cursor-pointer justify-between rounded-md border bg-white p-3"
							@click="toggleDropdown"
							:class="
								isDropdownOpen
									? 'border-primary-700 text-primary-700'
									: 'border-slate-300'
							"
						>
							{{ selectedPlan?.firstName }} {{ selectedPlan?.lastName }}

							<PlayIcon
								class="group-hover:text-primary-700 mt-1 h-4 w-4 transition-transform duration-300"
								:class="
									isDropdownOpen
										? 'text-primary-700 rotate-90'
										: '-rotate-90 text-slate-500'
								"
							/>
						</div>
						<div
							v-if="isDropdownOpen"
							class="absolute right-0 left-4 z-10 mt-2 rounded-md border border-slate-300 bg-white shadow-lg"
						>
							<div
								v-for="plan in sessionStore.activePlans ?? []"
								:key="plan.id"
								class="cursor-pointer p-3 hover:bg-slate-100"
								@click="selectPlan(plan)"
							>
								{{ plan.firstName }} {{ plan.lastName }}
							</div>
						</div>
					</div>

					<!-- Cart Content -->
					<transition
						name="slide-left"
						mode="out-in"
					>
						<RightSideBarShoppingCart
							v-if="activeTab === 'Cart'"
							:selectedPlanId="selectedPlanId"
						/>
					</transition>
				</div>
			</Transition>
		</div>

		<div
			class="*:text-primary-700! flex w-14 min-w-14 flex-col items-center gap-6 pt-4"
		>
			<!-- Cart -->
			<button
				:disabled="sessionDocumentsStore.currentPlanIsEncrypted"
				type="button"
				class="group relative size-5 disabled:cursor-not-allowed disabled:opacity-50"
				@click="handleClick('Cart')"
			>
				<ShoppingCartSolid
					class="absolute inset-0 size-5 transition-opacity"
					:class="
						activeTab === 'Cart'
							? 'opacity-100'
							: 'opacity-0 group-hover:opacity-100'
					"
				/>
				<ShoppingCartOutline
					class="absolute inset-0 size-5 transition-opacity"
					:class="
						activeTab === 'Cart'
							? 'opacity-0'
							: 'opacity-100 group-hover:opacity-0'
					"
				/>
			</button>
		</div>
	</section>
</template>

<script setup lang="ts">
import RightSideBarShoppingCart from './RightSideBarShoppingCart.vue'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { ShoppingCartIcon as ShoppingCartOutline } from '@heroicons/vue/24/outline'
import { ShoppingCartIcon as ShoppingCartSolid } from '@heroicons/vue/24/solid'
import { ChevronLeftIcon, PlayIcon } from '@heroicons/vue/24/solid'
import { ref, computed } from 'vue'

const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()

const selectedPlanId = ref(sessionStore.activePlans?.[0]?.id ?? '')
const isDropdownOpen = ref(false)
const activeTab = ref<'Cart'>('Cart')

// Get the selected plan
const selectedPlan = computed(() => {
	return sessionStore.activePlans?.find((p) => p.id === selectedPlanId.value)
})

/**
 * Toggle the dropdown menu for selecting plans.
 */
function toggleDropdown() {
	isDropdownOpen.value = !isDropdownOpen.value
}

/**
 * Set the selected plan and close the dropdown menu.
 *
 * @param {any} plan - The plan to select
 */
function selectPlan(plan: any) {
	selectedPlanId.value = plan.id
	isDropdownOpen.value = false
}

const props = defineProps<{ expanded: boolean }>()
const emit = defineEmits<{ (e: 'expand'): void }>()

function handleClick(tab: 'Cart') {
	activeTab.value = tab
	if (!props.expanded) emit('expand')
}
</script>
